import { Link } from "react-router-dom";
import { FileEdit, Boxes, MapPin, BadgeCheck, ArrowRight } from "lucide-react";

const STEPS = [
  {
    n: "01",
    icon: FileEdit,
    title: "Request a Quote",
    desc: "Tell us about your shipment — origin, destination, equipment and timeline. Takes 90 seconds.",
  },
  {
    n: "02",
    icon: Boxes,
    title: "Receive a Plan",
    desc: "Your dedicated dispatcher engineers the most efficient, cost-effective routing — same business day.",
  },
  {
    n: "03",
    icon: MapPin,
    title: "Track Your Shipment",
    desc: "Real-time GPS updates, proactive delay alerts, and a 24/7 line straight to your account manager.",
  },
  {
    n: "04",
    icon: BadgeCheck,
    title: "Successful Delivery",
    desc: "On-time, intact, and on brand — with proof of delivery and a debrief on every move.",
  },
];

export default function SimpleProcess() {
  return (
    <section
      data-testid="simple-process"
      className="relative bg-[#0a0a0a] py-16 sm:py-20 lg:py-24 overflow-hidden border-t border-white/5"
    >
      {/* Subtle grid backdrop */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, #ffffff 0, #ffffff 1px, transparent 1px, transparent 24px)",
        }}
      />

      <div className="container-mdgx relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-14 lg:mb-20">
          <div className="inline-flex items-center gap-3 mb-5">
            <span className="block w-8 h-px bg-[#D9633F]" />
            <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#D9633F]">How We Work</span>
            <span className="block w-8 h-px bg-[#D9633F]" />
          </div>
          <h2 className="font-display font-extrabold text-white text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.05]">
            From quote to delivery in <span className="text-[#D9633F]">four steps</span>.
          </h2>
          <p className="text-white/65 mt-5 leading-relaxed">
            No call-tag, no paperwork chaos. A clean, transparent process every single load.
          </p>
        </div>

        {/* 4-step horizontal flow */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-0 relative">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const isLast = i === STEPS.length - 1;
            return (
              <div
                key={s.n}
                data-testid={`process-step-${s.n}`}
                className="relative px-2 lg:px-6 group"
              >
                {/* Connector arrow between steps (desktop only) */}
                {!isLast && (
                  <div className="hidden lg:flex absolute top-9 -right-4 z-10 items-center text-[#D9633F]/40 group-hover:text-[#D9633F] transition-colors">
                    <ArrowRight size={20} />
                  </div>
                )}

                {/* Step number badge */}
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-18 h-18 relative">
                    <div className="w-[72px] h-[72px] bg-[#D9633F] flex items-center justify-center group-hover:scale-105 transition-transform">
                      <Icon size={28} className="text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 bg-white text-[#0a0a0a] font-display font-black text-xs px-2 py-0.5 tracking-wide">
                      {s.n}
                    </div>
                  </div>
                </div>

                <h3 className="font-display font-bold text-white text-lg sm:text-xl leading-tight mb-3">
                  {s.title}
                </h3>
                <p className="text-white/65 text-sm leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>

        {/* Footer CTA */}
        <div className="text-center mt-14 lg:mt-16">
          <Link
            to="/quote"
            data-testid="process-cta"
            className="inline-flex items-center gap-2 bg-[#D9633F] hover:bg-[#c2542f] text-white px-8 py-4 font-display font-bold tracking-wide text-sm transition-colors shadow-[0_8px_24px_rgba(217,99,63,0.35)]"
          >
            Start with step one — Request a quote
            <ArrowRight size={16} />
          </Link>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40 mt-4">
            Or call dispatch: <a href="tel:+19053637673" className="text-[#D9633F] hover:underline">+1 (905) 363-7673</a>
          </div>
        </div>
      </div>
    </section>
  );
}
